import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Loader2, BookMarked, Eye, Save, FileText, FileType, Palette, Sparkles, Image as ImageIcon, ChevronUp, ChevronDown, ChevronRight, ListOrdered, Download, Check, Circle, ArrowRight } from "lucide-react";

const COVER_THEMES = [
  { id: "warm-ivory", label: "Warm Ivory", bg: "#F7F3EC", fg: "#2b2b2b" },
  { id: "classic-teal", label: "Classic Teal", bg: "#0D4C53", fg: "#F7F3EC" },
  { id: "charcoal", label: "Charcoal", bg: "#2b2b2b", fg: "#F7F3EC" },
];

function Section({ id, title, icon: Icon, open, onToggle, children, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-line overflow-hidden" data-testid={testid}>
      <button data-testid={`${testid}-header`} onClick={() => onToggle(id)}
        className="w-full flex items-center gap-3 p-5 sm:p-6 text-left hover:bg-cream2/40 transition-colors">
        <span className="w-10 h-10 rounded-xl bg-sagelight text-teal flex items-center justify-center shrink-0"><Icon size={20} /></span>
        <span className="flex-1 font-serif text-xl text-charcoal">{title}</span>
        <ChevronRight size={20} className={`text-subtle transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && <div className="px-5 sm:px-6 pb-6 animate-fade-in">{children}</div>}
    </div>
  );
}

export default function Book() {
  const navigate = useNavigate();
  const location = useLocation();
  const [book, setBook] = useState(null);
  const [reading, setReading] = useState(!!location.state?.read);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState("");
  const [coverPrompt, setCoverPrompt] = useState("");
  const [genCover, setGenCover] = useState(false);
  const [uploadingAuthor, setUploadingAuthor] = useState(false);
  const [openSection, setOpenSection] = useState("details");
  const [timeline, setTimeline] = useState([]);

  const toggle = (id) => setOpenSection((cur) => (cur === id ? "" : id));

  const load = async () => {
    const [b, tl] = await Promise.all([api.get("/book"), api.get("/timeline").catch(() => ({ data: [] }))]);
    setBook(b.data);
    setTimeline((tl.data || []).filter((t) => t.confirmed && t.year));
  };
  useEffect(() => { load(); }, []);

  const generateCover = async () => {
    if (!coverPrompt.trim()) { toast.error("Describe the cover you'd like first."); return; }
    setGenCover(true);
    try {
      const { data } = await api.post("/book/cover/generate-ai", { prompt: coverPrompt.trim() });
      setBook((b) => ({ ...b, cover_ai_id: data.file_id, cover_mode: "ai" }));
      toast.success("Cover artwork created!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "We couldn't create the cover just now. Please try again.");
    } finally { setGenCover(false); }
  };

  const exportBook = async (format) => {
    setExporting(format);
    try {
      const res = await api.get("/book/export", { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(book?.working_title || "my-memoir").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.${format}`;
      document.body.appendChild(a); // required for the download to fire in all browsers
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(`Your memoir is downloading as ${format.toUpperCase()}`);
    } catch (e) {
      const msg = e.response?.status === 400
        ? "Create at least one chapter draft before downloading."
        : "We couldn't prepare that file just now. Please try again.";
      toast.error(msg);
    } finally { setExporting(""); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/book", {
        working_title: book.working_title, subtitle: book.subtitle,
        dedication: book.dedication, introduction: book.introduction, acknowledgements: book.acknowledgements,
        about_author: book.about_author, author_photo_id: book.author_photo_id, back_cover_portrait: book.back_cover_portrait,
        back_cover_blurb: book.back_cover_blurb,
        author_name: book.author_name, cover_template: book.cover_template,
        cover_mode: book.cover_mode, cover_photo_id: book.cover_photo_id, cover_ai_id: book.cover_ai_id,
      });
      toast.success("Your book details are saved");
    } catch { toast.error("Couldn't save just now. Please try again."); }
    finally { setSaving(false); }
  };

  const moveChapter = async (i, dir) => {
    const ids = book.chapters.map((c) => c.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      const { data } = await api.post("/chapters/reorder", { ordered_ids: ids });
      setBook((b) => ({ ...b, chapters: data }));
    } catch { toast.error("Couldn't reorder just now. Please try again."); }
  };
  const setChapterTitle = (i, v) => setBook((b) => {
    const chapters = [...b.chapters]; chapters[i] = { ...chapters[i], title: v }; return { ...b, chapters };
  });
  const saveChapterTitle = async (c) => {
    const t = (c.title || "").trim();
    if (!t) return;
    await api.put(`/chapters/${c.id}`, { title: t });
    toast.success("Chapter renamed");
  };
  const uploadAuthorPhoto = async (f) => {
    setUploadingAuthor(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/book/author-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBook((b) => ({ ...b, author_photo_id: data.file_id }));
      toast.success("Photo added");
    } catch { toast.error("Couldn't upload that image. Please try another."); }
    finally { setUploadingAuthor(false); }
  };

  if (!book) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal" size={28} /></div>;
  const set = (k, v) => setBook({ ...book, [k]: v });
  const Optional = () => <span className="ml-1 text-xs font-normal text-subtle">(optional)</span>;

  const chapterCount = (book.chapters || []).length;
  const hasCover = !!(book.cover_template || book.cover_mode === "ai" || book.cover_mode === "photo");
  const checklist = [
    { key: "chapters", label: "Review your chapters", done: chapterCount > 0, optional: false, section: "order", go: () => navigate("/story") },
    { key: "title", label: "Choose your title", done: !!(book.working_title || "").trim(), optional: false, section: "details" },
    { key: "photos", label: "Add photographs", done: false, optional: true, go: () => navigate("/photos") },
    { key: "dedication", label: "Add a dedication", done: !!(book.dedication || "").trim(), optional: true, section: "details" },
    { key: "intro", label: "Add an introduction", done: !!(book.introduction || "").trim(), optional: true, section: "details" },
    { key: "ack", label: "Add acknowledgements", done: !!(book.acknowledgements || "").trim(), optional: true, section: "details" },
    { key: "cover", label: "Choose a cover", done: hasCover, optional: true, section: "cover" },
    { key: "download", label: "Download your book", done: false, optional: false, section: "download" },
  ];
  const nextItem = checklist.find((c) => !c.optional && !c.done) || checklist.find((c) => c.key === "download");
  const openItem = (c) => { if (c.go) c.go(); else if (c.section) setOpenSection(c.section); };

  if (reading) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <button onClick={() => setReading(false)} className="text-subtle hover:text-charcoal text-sm mb-6">← Back to My Book</button>
        <div className="bg-white rounded-3xl border border-line p-8 sm:p-12">
          <h1 className="font-serif text-4xl text-charcoal text-center">{book.working_title}</h1>
          {book.subtitle && <p className="text-center text-subtle mt-2 text-lg">{book.subtitle}</p>}
          {book.dedication && <p className="italic text-center text-subtle mt-10 font-serif">{book.dedication}</p>}
          {book.introduction && <div className="mt-10"><h3 className="font-serif text-xl text-teal">Introduction</h3><p className="mt-2 whitespace-pre-wrap leading-relaxed">{book.introduction}</p></div>}
          {timeline.length > 0 && (
            <div className="mt-10" data-testid="read-timeline">
              <h3 className="font-serif text-xl text-teal">Life Timeline</h3>
              <div className="mt-3 space-y-1.5">
                {timeline.map((t, i) => (
                  <div key={t.id || i} className="flex gap-4">
                    <span className="font-serif text-teal w-16 shrink-0 text-right">{t.approx ? `~${t.year}` : t.year}</span>
                    <span className="text-charcoal leading-relaxed">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {book.chapters.map((c, i) => (
            <div key={c.id} className="mt-12" data-testid={`read-chapter-${i}`}>
              <p className="text-xs uppercase tracking-widest text-sage font-semibold">Chapter {i + 1}</p>
              <h2 className="font-serif text-2xl text-charcoal mt-1">{c.title}</h2>
              <p className="mt-3 whitespace-pre-wrap leading-relaxed font-serif text-lg text-charcoal">{c.edited_text || c.draft_text || "This chapter is still gathering memories."}</p>
            </div>
          ))}
          {book.acknowledgements && <div className="mt-12"><h3 className="font-serif text-xl text-teal">Acknowledgements</h3><p className="mt-2 whitespace-pre-wrap leading-relaxed">{book.acknowledgements}</p></div>}
          {book.about_author && <div className="mt-12" data-testid="read-about-author"><h3 className="font-serif text-xl text-teal">About the Author</h3>{book.author_photo_id && <img src={fileUrl(book.author_photo_id)} alt="The author" className="mt-3 h-28 w-28 rounded-full object-cover" />}<p className="mt-2 whitespace-pre-wrap leading-relaxed">{book.about_author}</p></div>}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5 animate-fade-in">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-charcoal flex items-center gap-3"><BookMarked className="text-sage" /> Finish and download my book</h1>
          <p className="text-subtle mt-1 text-sm">Work through the steps below. You can download a draft at any time — the extra details are all optional.</p>
        </div>
        <button data-testid="read-book-btn" onClick={() => setReading(true)} className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-5 py-2.5 rounded-full font-semibold text-sm">
          <Eye size={16} /> Read my memoir
        </button>
      </div>

      {/* Finishing checklist */}
      <div data-testid="finish-checklist" className="bg-white rounded-2xl border border-line p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-serif text-xl text-charcoal">Your finishing checklist</h2>
          {nextItem && (
            <button data-testid="checklist-next-btn" onClick={() => openItem(nextItem)}
              className="inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-4 py-2 rounded-full font-semibold text-sm transition-colors">
              {nextItem.key === "download" ? "Download your book" : `Next: ${nextItem.label}`} <ArrowRight size={15} />
            </button>
          )}
        </div>
        <p className="text-subtle text-sm mt-1">You can download your book at any time — the optional items are yours to add whenever you like.</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-2">
          {checklist.map((c) => (
            <li key={c.key}>
              <button data-testid={`checklist-${c.key}`} onClick={() => openItem(c)}
                className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-xl hover:bg-cream2 transition-colors">
                <span className={c.done ? "text-teal" : "text-line"}>{c.done ? <Check size={18} /> : <Circle size={18} />}</span>
                <span className={`flex-1 text-sm ${c.done ? "text-teal" : "text-charcoal"}`}>{c.label}{c.optional && <Optional />}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* 1. Book details */}
      <Section id="details" title="1 · Book details" icon={BookMarked} open={openSection === "details"} onToggle={toggle} testid="section-details">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-charcoal">Working title</label>
            <input data-testid="book-title-input" value={book.working_title || ""} onChange={(e) => set("working_title", e.target.value)} className="mt-1.5 w-full h-12 px-4 font-serif text-lg rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Subtitle<Optional /></label>
            <input data-testid="book-subtitle-input" value={book.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="mt-1.5 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Dedication<Optional /></label>
            <input data-testid="book-dedication-input" value={book.dedication || ""} onChange={(e) => set("dedication", e.target.value)} placeholder="For my grandchildren…" className="mt-1.5 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Introduction<Optional /></label>
            <textarea data-testid="book-intro-input" value={book.introduction || ""} onChange={(e) => set("introduction", e.target.value)} className="mt-1.5 w-full min-h-[100px] p-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Acknowledgements<Optional /></label>
            <textarea data-testid="book-acknowledgements-input" value={book.acknowledgements || ""} onChange={(e) => set("acknowledgements", e.target.value)} placeholder="Thank the people who matter to you…" className="mt-1.5 w-full min-h-[90px] p-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">About the Author<Optional /></label>
            <textarea data-testid="book-about-author-input" value={book.about_author || ""} onChange={(e) => set("about_author", e.target.value)} placeholder="A few lines about you — where you were born, your family, what you're known for. This appears at the back of your book." className="mt-1.5 w-full min-h-[110px] p-4 rounded-xl border border-line outline-none focus:border-teal" />
            <div className="mt-3 flex items-center gap-4">
              {book.author_photo_id && <img data-testid="author-photo-preview" src={fileUrl(book.author_photo_id)} alt="The author" className="h-20 w-20 rounded-full object-cover border border-line" />}
              <label className="inline-flex items-center gap-2 bg-cream2 hover:bg-sagelight text-charcoal px-4 py-2 rounded-full font-semibold text-sm cursor-pointer transition-colors">
                {uploadingAuthor ? <><Loader2 className="animate-spin" size={16} /> Adding photo…</> : <><ImageIcon size={16} /> {book.author_photo_id ? "Change photo" : "Add a photo of you"}</>}
                <span className="text-xs font-normal text-subtle">(optional)</span>
                <input data-testid="author-photo-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadAuthorPhoto(e.target.files[0])} />
              </label>
            </div>
            {book.author_photo_id && (
              <label className="mt-3 flex items-center gap-2 text-sm text-charcoal cursor-pointer">
                <input data-testid="back-cover-toggle" type="checkbox" checked={!!book.back_cover_portrait} onChange={(e) => set("back_cover_portrait", e.target.checked)} className="w-4 h-4 accent-teal" />
                Also use this photo as a portrait on the back cover
              </label>
            )}
            <label className="text-sm font-medium text-charcoal block mt-4">Back-cover description<Optional /></label>
            <textarea data-testid="book-back-blurb-input" value={book.back_cover_blurb || ""} onChange={(e) => set("back_cover_blurb", e.target.value)} placeholder="A sentence or two for the back of the book — what your memoir is about, or a line you'd like readers to see first." className="mt-1.5 w-full min-h-[80px] p-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <button data-testid="book-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
            {saving ? <><Loader2 className="animate-spin" size={16} /> Saving your details…</> : <><Save size={16} /> Save details</>}
          </button>
        </div>
      </Section>

      {/* 2. Cover */}
      <Section id="cover" title="2 · Cover" icon={Palette} open={openSection === "cover"} onToggle={toggle} testid="section-cover">
        <div className="space-y-5" data-testid="cover-designer">
          <div>
            <label className="text-sm font-medium text-charcoal">Author name (shown on the cover)<Optional /></label>
            <input data-testid="book-author-input" value={book.author_name || ""} onChange={(e) => set("author_name", e.target.value)} placeholder="e.g. Juliet Sullivan" className="mt-1.5 w-full h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Choose a style</label>
            <div className="mt-2 flex flex-wrap gap-3">
              {COVER_THEMES.map((t) => (
                <button key={t.id} data-testid={`cover-theme-${t.id}`} onClick={() => set("cover_template", t.id)}
                  className={`w-24 h-32 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all ${book.cover_template === t.id ? "border-teal ring-2 ring-teal/30" : "border-line hover:border-sage"}`}
                  style={{ background: t.bg, color: t.fg }}>
                  <span className="font-serif text-sm px-2 text-center leading-tight">{book.working_title || "My Memoir"}</span>
                  <span className="text-[10px] opacity-70 mt-2">{t.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="pt-2 border-t border-line">
            <label className="text-sm font-medium text-charcoal flex items-center gap-1.5"><Sparkles size={15} className="text-coral" /> Add AI cover artwork<Optional /></label>
            <p className="text-xs text-subtle mt-1">Describe a scene or mood and we'll create bespoke cover art (no text — your title sits on top).</p>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <input data-testid="cover-ai-prompt" value={coverPrompt} onChange={(e) => setCoverPrompt(e.target.value)} placeholder="e.g. a quiet English seaside town at golden hour, watercolour" className="flex-1 h-11 px-4 rounded-xl border border-line outline-none focus:border-teal" />
              <button data-testid="cover-ai-generate-btn" onClick={generateCover} disabled={genCover} className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coraldark text-white px-5 h-11 rounded-full font-semibold text-sm disabled:opacity-60 whitespace-nowrap">
                {genCover ? <><Loader2 className="animate-spin" size={15} /> Creating your cover…</> : <><Sparkles size={15} /> Generate art</>}
              </button>
            </div>
            {book.cover_ai_id && (
              <div className="mt-3 flex items-center gap-3">
                <img data-testid="cover-ai-preview" src={fileUrl(book.cover_ai_id)} alt="AI cover" className="h-28 w-auto rounded-lg border border-line object-cover" />
                <label className="flex items-center gap-2 text-sm text-charcoal">
                  <input type="checkbox" data-testid="cover-use-ai" checked={book.cover_mode === "ai"} onChange={(e) => set("cover_mode", e.target.checked ? "ai" : "template")} />
                  Use this artwork on the cover
                </label>
              </div>
            )}
          </div>
          <button data-testid="cover-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-6 py-2.5 rounded-full font-semibold disabled:opacity-60">
            {saving ? <><Loader2 className="animate-spin" size={16} /> Saving your cover…</> : <><Save size={16} /> Save cover</>}
          </button>
        </div>
      </Section>

      {/* 3. Chapter order */}
      <Section id="order" title="3 · Chapter order" icon={ListOrdered} open={openSection === "order"} onToggle={toggle} testid="section-order">
        {book.chapters.length === 0 ? (
          <p className="text-subtle">No chapters yet. Head to <button onClick={() => navigate("/story")} className="text-teal font-semibold underline">My Book</button> to turn your memories into chapters.</p>
        ) : (
          <>
            {book.chapters.length > 1 && <p className="text-subtle text-sm mb-3">Use the arrows to set the order your chapters appear in the finished book.</p>}
            <div className="space-y-3">
              {book.chapters.map((c, i) => (
                <div key={c.id} data-testid={`book-chapter-${i}`} className="flex items-center gap-3 bg-cream2 rounded-2xl border border-line p-4 animate-fade-up">
                  <div className="flex flex-col">
                    <button data-testid={`chapter-up-${i}`} onClick={() => moveChapter(i, -1)} disabled={i === 0}
                      className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move up"><ChevronUp size={18} /></button>
                    <button data-testid={`chapter-down-${i}`} onClick={() => moveChapter(i, 1)} disabled={i === book.chapters.length - 1}
                      className="text-subtle hover:text-teal disabled:opacity-30 transition-colors" title="Move down"><ChevronDown size={18} /></button>
                  </div>
                  <span className="font-serif text-teal text-lg w-6 shrink-0 text-center">{i + 1}</span>
                  <input data-testid={`chapter-rename-${i}`} value={c.title || ""} onChange={(e) => setChapterTitle(i, e.target.value)} onBlur={() => saveChapterTitle(c)}
                    className="flex-1 font-serif text-lg text-charcoal bg-transparent border-b border-transparent hover:border-line focus:border-teal outline-none py-1" />
                  <button data-testid={`chapter-open-${i}`} onClick={() => navigate(`/story/${c.id}`)} className="text-sm font-semibold text-teal hover:underline shrink-0">Open</button>
                </div>
              ))}
            </div>
          </>
        )}
      </Section>

      {/* 4. Download */}
      <Section id="download" title="4 · Download" icon={Download} open={openSection === "download"} onToggle={toggle} testid="section-download">
        <p className="text-subtle text-sm mb-4">Download your book — title, dedication, introduction and every drafted chapter — as a document to keep, share or print. You don't need to complete the optional details first.</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="export-docx-btn" onClick={() => exportBook("docx")} disabled={!!exporting}
            className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
            {exporting === "docx" ? <><Loader2 className="animate-spin" size={15} /> Preparing your Word document…</> : <><FileType size={15} /> Download Word</>}
          </button>
          <button data-testid="export-pdf-btn" onClick={() => exportBook("pdf")} disabled={!!exporting}
            className="inline-flex items-center gap-2 bg-white border border-line hover:border-sage text-charcoal px-5 py-2.5 rounded-full font-semibold text-sm disabled:opacity-60">
            {exporting === "pdf" ? <><Loader2 className="animate-spin" size={15} /> Preparing your PDF…</> : <><FileText size={15} /> Download PDF</>}
          </button>
        </div>
        <p className="mt-4 text-subtle text-sm">Your PDF is print-ready (6×9″ book size, cover, page numbers and your photos). You can send it to any printer, or to a company that prints books one at a time — built-in ordering is coming soon.</p>
      </Section>
    </div>
  );
}
